import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  ProviderFeatureEnvironment,
  providerFeatureEnvironment,
} from '@fmnts/common';
import { logger } from '@fmnts/common/log';
import * as P from 'effect/Predicate';
import { catchError, EMPTY } from 'rxjs';
import { ScriptConfig, ScriptLoaderService } from './script-loader.service';

export enum BrowserFeatureKind {
  Script,
}

export type BrowserFeature<TKind extends BrowserFeatureKind> =
  ProviderFeatureEnvironment<TKind>;

const { make: makeBrowserFeature } =
  providerFeatureEnvironment<BrowserFeatureKind>();

/**
 * Environment Provider function to enhance the browser features,
 * can be injected at:
 * - Modules
 * - Routes
 *
 * @param features `withScript`, ...
 */
export function provideCommonBrowser(
  ...features: BrowserFeature<BrowserFeatureKind>[]
): EnvironmentProviders {
  return makeEnvironmentProviders([...features.flatMap((f) => f.providers)]);
}

/**
 * This can be used to add external scripts that should be loaded
 * for an entire feature/module and it's not urgent to handle the error
 * response. For other cases use the {@link ScriptLoaderService} directly
 * within a component.
 *
 * @param opts `src`, `id`, `async`, `defer`
 * @returns
 */
export function withScript(
  config: ScriptConfig | (() => ScriptConfig),
): BrowserFeature<BrowserFeatureKind.Script> {
  return makeBrowserFeature(BrowserFeatureKind.Script, [
    provideEnvironmentInitializer(
      (
        scriptLoader = inject(ScriptLoaderService),
        log = logger({ name: 'ScriptLoader' }),
      ) => {
        const cfg = P.isFunction(config) ? config() : config;
        scriptLoader
          .loadScript(cfg)
          .pipe(
            catchError((e) => {
              log.warn(`Failed to load script: '${cfg.id}'`);
              return EMPTY;
            }),
          )
          .subscribe();
      },
    ),
  ]);
}
